/**
 * Кабинет агроциклов хокимията.
 *
 * Экран отвечает на три вопроса, и они разные, поэтому это три вкладки, а не
 * три колонки одной таблицы:
 *
 * * **Шаблоны** — по какому плану вообще идут циклы этой культуры и какие
 *   работы уже открыты. Здесь живёт хребет последовательности: работа
 *   открывается решением, а не наступлением даты;
 * * **Циклы** — что происходит на конкретной земле: сколько работ ждут,
 *   сколько просрочено, чем этот цикл отличается от шаблона;
 * * **К запуску** — где цикл ещё не начат. Предварительного цикла в базе нет,
 *   и эта вкладка — единственное место, где он существует.
 *
 * Чего экран не делает: не считает состояния работ (их считает сервер) и не
 * проверяет права (их проверяет доменный сервис). Спрятанная кнопка ничего не
 * защищает, поэтому она прячется только ради спокойствия глаз.
 */

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { useAuth } from "../../auth/context";
import {
  api,
  type AgrocycleCandidates,
  type AgrocycleCatalog,
  type AgrocycleCycleDetail,
  type AgrocycleCycles,
  type AgrocycleTemplateDetail,
  type AgrocycleTemplates,
  type CandidateRow,
  type SlotOut,
  type TemplateRow,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { PageHeader, layout } from "../../components/AppShell";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { Select } from "../../components/Select";
import { Tab, TabList, TabPanel, Tabs } from "../../components/Tabs";
import { TextField } from "../../components/TextField";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  EvidenceBadge,
  InlineAlert,
  MetricCard,
  NotAppliedNotice,
  RequestErrorPanel,
  ScopeNotice,
  Text,
} from "../../components/ui";
import { Timeline, type TimelineRow } from "../../components/Timeline";
import { formatDate } from "../../lib/format";
import { levelPath } from "../../lib/levels";
import { cropLabel } from "../../lib/crops";
import features from "../features.module.css";
import styles from "./agrocycle.module.css";
import {
  availabilityAction,
  demanding,
  frozenNote,
  lifecycleView,
  normsView,
  slotStateView,
  startHint,
  stateBuckets,
  windowView,
} from "./agrocycleModel";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { COMMON } from "../../i18n/common";
import { useT } from "../../i18n/hooks";

const m = defineMessages({
  tabTemplates: {
    id: "canonAgrocycle.tabs.templates",
    defaultMessage: "Шаблоны и доступность работ",
    description: "Название вкладки кабинета агроциклов (менеджер хокимията): шаблоны и открытие работ",
  },
  tabCycles: {
    id: "canonAgrocycle.tabs.cycles",
    defaultMessage: "Начатые агроциклы",
    description: "Название вкладки кабинета агроциклов: агроциклы, которые уже начаты",
  },
  tabCandidates: {
    id: "canonAgrocycle.tabs.candidates",
    defaultMessage: "К началу",
    description: "Название вкладки кабинета агроциклов: контуры, где агроцикл ещё можно начать; коротко",
  },
  tabsLabel: {
    id: "canonAgrocycle.tabs.label",
    defaultMessage: "Виды агроциклов",
    description: "Подпись (для чтения с экрана) переключателя вкладок кабинета агроциклов",
  },
  title: {
    id: "canonAgrocycle.title",
    defaultMessage: "Агроциклы",
    description: "Заголовок экрана агроциклов (менеджер хокимията) и последний пункт цепочки навигации",
  },
  purpose: {
    id: "canonAgrocycle.purpose",
    defaultMessage: "План агроцикла и его исполнение. Последовательность работ держится не цепочкой зависимостей, а доступностью: следующая работа закрыта, пока хокимият её не откроет. Начатый агроцикл держит свою копию плана и дальше за шаблоном не следует.",
    description: "Пояснение под заголовком экрана агроциклов; хокимият — администрация района",
  },
  tplFailed: {
    id: "canonAgrocycle.templates.failed",
    defaultMessage: "Шаблоны не загрузились",
    description: "Заголовок ошибки загрузки шаблонов агроцикла",
  },
  tplLoading: {
    id: "canonAgrocycle.templates.loading",
    defaultMessage: "Читаем шаблоны",
    description: "Заголовок индикатора загрузки шаблонов",
  },
  tplEmpty: {
    id: "canonAgrocycle.templates.empty",
    defaultMessage: "Шаблонов пока нет",
    description: "Заголовок пустого списка шаблонов агроцикла",
  },
  tplEmptyText: {
    id: "canonAgrocycle.templates.emptyText",
    defaultMessage: "Шаблон агроцикла — это список работ культуры по этапам. Пока владелец не зафиксировал техкарты, начинать агроциклы не по чему, и это честное «данных нет», а не сбой.",
    description: "Пояснение к пустому списку шаблонов; техкарта — технологическая карта возделывания культуры",
  },
  tplCaption: {
    id: "canonAgrocycle.templates.caption",
    defaultMessage: "Действующий шаблон на культуру и сезон ровно один: именно его копию получает начатый агроцикл. «Открыто» — сколько работ шаблона открыто районным решением на дату контекста.",
    description: "Подпись (для чтения с экрана) таблицы шаблонов",
  },
  colCrop: {
    id: "canonAgrocycle.templates.colCrop",
    defaultMessage: "Культура",
    description: "Заголовок колонки: культура",
  },
  colSeason: {
    id: "canonAgrocycle.templates.colSeason",
    defaultMessage: "Сезон",
    description: "Заголовок колонки: сезон (год)",
  },
  colTemplate: {
    id: "canonAgrocycle.templates.colTemplate",
    defaultMessage: "Шаблон",
    description: "Заголовок колонки: название шаблона агроцикла",
  },
  colStatus: {
    id: "canonAgrocycle.templates.colStatus",
    defaultMessage: "Статус",
    description: "Заголовок колонки: статус шаблона (черновик, действует, в архиве)",
  },
  colActiveFrom: {
    id: "canonAgrocycle.templates.colActiveFrom",
    defaultMessage: "Действует с",
    description: "Заголовок колонки: с какой даты шаблон действует",
  },
  colWorks: {
    id: "canonAgrocycle.templates.colWorks",
    defaultMessage: "Работ",
    description: "Заголовок узкой числовой колонки: сколько работ в шаблоне",
  },
  colOpen: {
    id: "canonAgrocycle.templates.colOpen",
    defaultMessage: "Открыто",
    description: "Заголовок узкой числовой колонки: сколько работ шаблона открыто",
  },
  colCycles: {
    id: "canonAgrocycle.templates.colCycles",
    defaultMessage: "Агроциклов",
    description: "Заголовок узкой числовой колонки: сколько агроциклов идёт по шаблону",
  },
  collapse: {
    id: "canonAgrocycle.collapse",
    defaultMessage: "Свернуть",
    description: "Кнопка: свернуть раскрытую карточку; коротко",
  },
  showWorks: {
    id: "canonAgrocycle.templates.showWorks",
    defaultMessage: "Работы",
    description: "Кнопка в строке шаблона: показать его работы; коротко",
  },
  decisionNotSaved: {
    id: "canonAgrocycle.templates.decisionNotSaved",
    defaultMessage: "решение не записано",
    description: "Сообщение, когда ошибка не пришла с текстом: решение об открытии/закрытии работы не записано",
  },
  tplDetailFailed: {
    id: "canonAgrocycle.templates.detailFailed",
    defaultMessage: "Шаблон не загрузился",
    description: "Заголовок ошибки загрузки одного шаблона",
  },
  tplDetailLoading: {
    id: "canonAgrocycle.templates.detailLoading",
    defaultMessage: "Читаем работы шаблона",
    description: "Заголовок индикатора загрузки работ шаблона",
  },
  tplTitleCrop: {
    id: "canonAgrocycle.templates.titleCrop",
    defaultMessage: "Шаблон: {crop}",
    description: "Заголовок карточки шаблона; {crop} — название культуры",
  },
  tplTitleNoCrop: {
    id: "canonAgrocycle.templates.titleNoCrop",
    defaultMessage: "Шаблон: без культуры",
    description: "Заголовок карточки шаблона, не привязанного к культуре",
  },
  frozenNote: {
    id: "canonAgrocycle.templates.frozenNote",
    defaultMessage: "{hint}. Правка такого шаблона — это копия в новый черновик и ввод его в действие, а не изменение существующего: иначе план поменялся бы у тех, кто по нему уже отчитался.",
    description: "Пояснение к неизменяемому шаблону; {hint} — статус-подсказка («не меняется: …»)",
  },
  manageTitle: {
    id: "canonAgrocycle.templates.manageTitle",
    defaultMessage: "Доступностью работ управляет хокимият",
    description: "Заголовок предупреждения для пользователя без прав менеджера",
  },
  manageText: {
    id: "canonAgrocycle.templates.manageText",
    defaultMessage: "Открыть или заблокировать работу может аккаунт с ролью менеджера хокимията и районной областью. Кнопки спрятаны для спокойствия глаз — защищает не они, а сервер.",
    description: "Текст предупреждения для пользователя без прав менеджера",
  },
  decisionFailed: {
    id: "canonAgrocycle.templates.decisionFailed",
    defaultMessage: "Решение не записано",
    description: "Заголовок ошибки: решение об открытии/закрытии работы не записано; ниже — текст ошибки",
  },
  required: {
    id: "canonAgrocycle.work.required",
    defaultMessage: "обязательна",
    description: "Пометка у работы плана: обязательна; строчными",
  },
  optional: {
    id: "canonAgrocycle.work.optional",
    defaultMessage: "необязательна",
    description: "Пометка у работы плана: необязательна; строчными",
  },
  codeLine: {
    id: "canonAgrocycle.work.codeLine",
    defaultMessage: "{code} · {required}",
    description: "Строка под названием работы плана; {code} — код вида работы, {required} — «обязательна» или «необязательна»",
  },
  closeReasonLabel: {
    id: "canonAgrocycle.work.closeReasonLabel",
    defaultMessage: "Причина закрытия работы",
    description: "Подпись (для чтения с экрана) поля причины закрытия работы",
  },
  closeReasonPlaceholder: {
    id: "canonAgrocycle.work.closeReasonPlaceholder",
    defaultMessage: "почему закрываем",
    description: "Подсказка в поле причины закрытия работы",
  },
  confirm: {
    id: "canonAgrocycle.work.confirm",
    defaultMessage: "Подтвердить",
    description: "Кнопка: подтвердить закрытие работы; коротко",
  },
  normsTitle: {
    id: "canonAgrocycle.work.normsTitle",
    defaultMessage: "Нормы и происхождение",
    description: "Заголовок раскрытия под работой плана: нормы на гектар и откуда взята работа",
  },
  normsNone: {
    id: "canonAgrocycle.work.normsNone",
    defaultMessage: "нормы на гектар не заданы",
    description: "Подпись-сводка раскрытия «Нормы…», когда норм нет",
  },
  normsCount: {
    id: "canonAgrocycle.work.normsCount",
    defaultMessage: "{count, plural, one {# норма} few {# нормы} many {# норм} other {# нормы}} на гектар",
    description: "Подпись-сводка раскрытия «Нормы…»; {count} — число норм",
  },
  normsEmptyText: {
    id: "canonAgrocycle.work.normsEmptyText",
    defaultMessage: "Нормы на гектар по этой работе не заданы. Это отсутствие норматива, а не ноль: подставить сюда числа из черновика техкарты значило бы выдать гипотезу за норму.",
    description: "Пояснение в раскрытии «Нормы…», когда норм нет",
  },
  origin: {
    id: "canonAgrocycle.work.origin",
    defaultMessage: "Откуда работа: {where}",
    description: "Строка в раскрытии «Нормы…»; {where} — откуда взята работа (текст) или «не указано»",
  },
  originSource: {
    id: "canonAgrocycle.work.originSource",
    defaultMessage: "Откуда работа: {where} · источник: {source}",
    description: "Строка в раскрытии «Нормы…»; {where} — откуда взята работа, {source} — источник",
  },
  notSpecified: {
    id: "canonAgrocycle.work.notSpecified",
    defaultMessage: "не указано",
    description: "Подставляется, когда неизвестно, откуда взята работа; строчными",
  },
  seasonLabel: {
    id: "canonAgrocycle.season.label",
    defaultMessage: "Сезон",
    description: "Подпись фильтра по сезону",
  },
  seasonExplain: {
    id: "canonAgrocycle.season.explain",
    defaultMessage: "Фильтр ВИДА, а не срез. Он не меняет, какие версии утверждений существуют (это делает дата состояния), — он сужает популяцию, по которой считается знаменатель.",
    description: "Пояснение к фильтру по сезону; срез — снимок данных на дату",
  },
  seasonAll: {
    id: "canonAgrocycle.season.all",
    defaultMessage: "все сезоны",
    description: "Первый вариант фильтра по сезону и подсказка в пустом поле: без отбора; строчными",
  },
  seasonAllHint: {
    id: "canonAgrocycle.season.allHint",
    defaultMessage: "выборка не сужена",
    description: "Подсказка у варианта «все сезоны»",
  },
  seasonNoCycles: {
    id: "canonAgrocycle.season.noCycles",
    defaultMessage: "агроциклов нет",
    description: "Подсказка у сезона в фильтре: по нему нет начатых агроциклов",
  },
  seasonDescription: {
    id: "canonAgrocycle.season.description",
    defaultMessage: "Метка сезона агроцикла. Перечислены сезоны, по которым начатые агроциклы в этом срезе есть; выбранный стоит в списке всегда.",
    description: "Описание под фильтром по сезону",
  },
  cyclesFailed: {
    id: "canonAgrocycle.cycles.failed",
    defaultMessage: "Агроциклы не загрузились",
    description: "Заголовок ошибки загрузки списка агроциклов",
  },
  cyclesLoading: {
    id: "canonAgrocycle.cycles.loading",
    defaultMessage: "Читаем начатые агроциклы",
    description: "Заголовок индикатора загрузки списка агроциклов",
  },
  cyclesEmpty: {
    id: "canonAgrocycle.cycles.empty",
    defaultMessage: "Начатых агроциклов нет",
    description: "Заголовок пустого списка агроциклов",
  },
  cyclesEmptyText: {
    id: "canonAgrocycle.cycles.emptyText",
    defaultMessage: "Агроцикл появляется, когда хокимият начинает его на контуре. До этого он существует только как предварительный вид на вкладке «К началу» — в базе такой строки нет намеренно.",
    description: "Пояснение к пустому списку агроциклов, когда сервер не прислал своей причины",
  },
  cyclesCaption: {
    id: "canonAgrocycle.cycles.caption",
    defaultMessage: "«Ждут» — работы в состояниях «требуется» и «просрочена»: ровно то, что сейчас требует движения от хозяйства. Остальные состояния раскрываются в плане агроцикла.",
    description: "Подпись (для чтения с экрана) таблицы агроциклов",
  },
  colContour: {
    id: "canonAgrocycle.cycles.colContour",
    defaultMessage: "Контур",
    description: "Заголовок колонки: номер контура",
  },
  colYear: {
    id: "canonAgrocycle.cycles.colYear",
    defaultMessage: "Год",
    description: "Заголовок колонки: год агроцикла",
  },
  colStarted: {
    id: "canonAgrocycle.cycles.colStarted",
    defaultMessage: "Начат",
    description: "Заголовок колонки: дата начала агроцикла",
  },
  colArea: {
    id: "canonAgrocycle.cycles.colArea",
    defaultMessage: "Площадь",
    description: "Заголовок колонки: площадь контура",
  },
  colWaiting: {
    id: "canonAgrocycle.cycles.colWaiting",
    defaultMessage: "Ждут",
    description: "Заголовок колонки: сколько работ ждут выполнения",
  },
  colDeviations: {
    id: "canonAgrocycle.cycles.colDeviations",
    defaultMessage: "Отклонений",
    description: "Заголовок колонки: число отклонений от шаблона",
  },
  plan: {
    id: "canonAgrocycle.cycles.plan",
    defaultMessage: "План",
    description: "Кнопка в строке агроцикла: показать его план; коротко",
  },
  kpiStarted: {
    id: "canonAgrocycle.cycles.kpi.started",
    defaultMessage: "Агроциклов начато",
    description: "Подпись KPI: сколько агроциклов начато",
  },
  kpiStartedCut: {
    id: "canonAgrocycle.cycles.kpi.startedCut",
    defaultMessage: "на экране {shown}: выдача обрезана страницей",
    description: "Мелкая подпись под KPI «Агроциклов начато»; {shown} — сколько на экране",
  },
  kpiStartedAll: {
    id: "canonAgrocycle.cycles.kpi.startedAll",
    defaultMessage: "все агроциклы среза на экране",
    description: "Мелкая подпись под KPI «Агроциклов начато»; срез — снимок данных на дату",
  },
  kpiWaiting: {
    id: "canonAgrocycle.cycles.kpi.waiting",
    defaultMessage: "Работ ждут",
    description: "Подпись KPI: сколько работ ждут выполнения хозяйством",
  },
  kpiWaitingOverdue: {
    id: "canonAgrocycle.cycles.kpi.waitingOverdue",
    defaultMessage: "из них просрочено: {count}",
    description: "Мелкая подпись под KPI «Работ ждут»; {count} — число просроченных",
  },
  kpiWaitingExplain: {
    id: "canonAgrocycle.cycles.kpi.waitingExplain",
    defaultMessage: "«Ждут» — работы в состояниях «требуется» и «просрочена»: ровно то, что требует движения от хозяйства сейчас. Заблокированная работа сюда не входит — её ещё не открыли, и спрашивать не с чего.",
    description: "Пояснение к KPI «Работ ждут»",
  },
  kpiDeviations: {
    id: "canonAgrocycle.cycles.kpi.deviations",
    defaultMessage: "Отклонений от шаблона",
    description: "Подпись KPI: сколько решений отличают агроциклы от шаблона",
  },
  kpiDeviationsNote: {
    id: "canonAgrocycle.cycles.kpi.deviationsNote",
    defaultMessage: "отменили, добавили или сдвинули срок — решением с причиной",
    description: "Мелкая подпись под KPI «Отклонений от шаблона»",
  },
  kpiDeviationsExplain: {
    id: "canonAgrocycle.cycles.kpi.deviationsExplain",
    defaultMessage: "Отклонение — это решение по конкретному агроциклу, а не ошибка. Шаблон при этом не правится: начатый агроцикл держит свою копию плана.",
    description: "Пояснение к KPI «Отклонений от шаблона»",
  },
  kpiArea: {
    id: "canonAgrocycle.cycles.kpi.area",
    defaultMessage: "Площадь под агроциклами",
    description: "Подпись KPI: суммарная площадь контуров, на которых идут агроциклы",
  },
  kpiAreaMissing: {
    id: "canonAgrocycle.cycles.kpi.areaMissing",
    defaultMessage: "без строки площади: {count, plural, one {# агроцикл} few {# агроцикла} many {# агроциклов} other {# агроцикла}} — в сумму они не вошли",
    description: "Мелкая подпись под KPI «Площадь под агроциклами»; {count} — число агроциклов",
  },
  kpiAreaAll: {
    id: "canonAgrocycle.cycles.kpi.areaAll",
    defaultMessage: "площадь известна по всем агроциклам показанной страницы",
    description: "Мелкая подпись под KPI «Площадь под агроциклами»",
  },
  planFailed: {
    id: "canonAgrocycle.cycles.planFailed",
    defaultMessage: "План агроцикла не загрузился",
    description: "Заголовок ошибки загрузки плана агроцикла",
  },
  planLoading: {
    id: "canonAgrocycle.cycles.planLoading",
    defaultMessage: "Читаем план агроцикла",
    description: "Заголовок индикатора загрузки плана агроцикла",
  },
  cycleTitle: {
    id: "canonAgrocycle.cycles.cycleTitle",
    defaultMessage: "Агроцикл на контуре {no} · {crop}",
    description: "Заголовок карточки агроцикла; {no} — номер контура, {crop} — культура",
  },
  noCrop: {
    id: "canonAgrocycle.cycles.noCrop",
    defaultMessage: "без культуры",
    description: "Подпись, когда у агроцикла или шаблона нет культуры; строчными",
  },
  frozenTemplateHint: {
    id: "canonAgrocycle.cycles.frozenTemplateHint",
    defaultMessage: "копия шаблона на момент начала агроцикла",
    description: "Подсказка у бейджа с названием шаблона",
  },
  templateBadge: {
    id: "canonAgrocycle.cycles.templateBadge",
    defaultMessage: "шаблон {name}",
    description: "Бейдж на карточке агроцикла; {name} — название шаблона",
  },
  startedOn: {
    id: "canonAgrocycle.cycles.startedOn",
    defaultMessage: "начат {date}",
    description: "Бейдж на карточке агроцикла; {date} — дата начала",
  },
  finishedOn: {
    id: "canonAgrocycle.cycles.finishedOn",
    defaultMessage: "завершён {date}",
    description: "Бейдж на карточке агроцикла; {date} — дата завершения",
  },
  deviationsTitle: {
    id: "canonAgrocycle.cycles.deviationsTitle",
    defaultMessage: "Чем этот агроцикл отличается от шаблона",
    description: "Заголовок карточки с отклонениями от шаблона",
  },
  authorUnknown: {
    id: "canonAgrocycle.cycles.authorUnknown",
    defaultMessage: "автор неизвестен",
    description: "Пометка в журнале решений: автор не записан; строчными",
  },
  logTitle: {
    id: "canonAgrocycle.cycles.logTitle",
    defaultMessage: "Кто и когда открывал работы",
    description: "Заголовок карточки с журналом решений об открытии работ",
  },
  logOpened: {
    id: "canonAgrocycle.cycles.logOpened",
    defaultMessage: "открыта",
    description: "Бейдж в журнале: работа открыта; строчными, коротко",
  },
  logBlocked: {
    id: "canonAgrocycle.cycles.logBlocked",
    defaultMessage: "заблокирована",
    description: "Бейдж в журнале: работа заблокирована; строчными, коротко",
  },
  logCycle: {
    id: "canonAgrocycle.cycles.logCycle",
    defaultMessage: "решение по этому агроциклу",
    description: "Область решения в журнале: только этот агроцикл",
  },
  logDistrict: {
    id: "canonAgrocycle.cycles.logDistrict",
    defaultMessage: "по району",
    description: "Область решения в журнале: весь район",
  },
  availOpenHint: {
    id: "canonAgrocycle.cycles.availHint",
    defaultMessage: "Доступность — решение, а не дата: на оси времени её отложить нечем",
    description: "Подсказка у бейджа доступности работы на оси времени",
  },
  availOpen: {
    id: "canonAgrocycle.cycles.availOpen",
    defaultMessage: "открыта",
    description: "Бейдж доступности работы на оси времени: открыта; строчными, коротко",
  },
  availClosed: {
    id: "canonAgrocycle.cycles.availClosed",
    defaultMessage: "закрыта",
    description: "Бейдж доступности работы на оси времени: закрыта; строчными, коротко",
  },
  shiftedNote: {
    id: "canonAgrocycle.cycles.shiftedNote",
    defaultMessage: "срок сдвинут отклонением этого агроцикла",
    description: "Пометка у работы на оси времени: срок сдвинут решением",
  },
  windowsTitle: {
    id: "canonAgrocycle.cycles.windows.title",
    defaultMessage: "Окна работ агроцикла",
    description: "Заголовок оси времени с окнами работ агроцикла",
  },
  windowsDenominator: {
    id: "canonAgrocycle.cycles.windows.denominator",
    defaultMessage: "{count, plural, one {# работа плана} few {# работы плана} many {# работ плана} other {# работы плана}}",
    description: "Знаменатель оси времени: сколько работ плана на оси; {count} — число",
  },
  windowsNoAsOf: {
    id: "canonAgrocycle.cycles.windows.noAsOf",
    defaultMessage: "срез не зафиксирован: линию даты рисовать не по чему",
    description: "Пометка на оси времени: даты среза нет (срез — снимок данных на дату)",
  },
  windowsEmpty: {
    id: "canonAgrocycle.cycles.windows.empty",
    defaultMessage: "Ни у одной работы плана нет полного окна: сроки либо не заданы, либо считаются от внешнего события, которого ещё нет.",
    description: "Пустое состояние оси времени окон работ",
  },
  windowsCaption: {
    id: "canonAgrocycle.cycles.windows.caption",
    defaultMessage: "Полоса — СРОК работы. Доступность на оси не откладывается: работу открывает решение, а не наступление даты, поэтому она стоит отметкой у имени. Вертикальная линия — дата среза, а не сегодняшний день.",
    description: "Подпись (для чтения с экрана) оси времени окон работ",
  },
  windowsNoBand: {
    id: "canonAgrocycle.cycles.windows.noBand",
    defaultMessage: "Без полосы: {count, plural, one {# работа} few {# работы} many {# работ} other {# работы}} — у них срока нет либо он не посчитан, и это разные вещи.",
    description: "Добавка к подписи оси времени; {count} — число работ без полосы",
  },
  startFailed: {
    id: "canonAgrocycle.candidates.startFailed",
    defaultMessage: "агроцикл не начат",
    description: "Сообщение, когда ошибка не пришла с текстом: агроцикл начать не удалось",
  },
  candFailed: {
    id: "canonAgrocycle.candidates.failed",
    defaultMessage: "Контуры к началу не загрузились",
    description: "Заголовок ошибки загрузки списка контуров, где можно начать агроцикл",
  },
  candLoading: {
    id: "canonAgrocycle.candidates.loading",
    defaultMessage: "Собираем предварительный вид",
    description: "Заголовок индикатора загрузки вкладки «К началу»",
  },
  kpiNoCycle: {
    id: "canonAgrocycle.candidates.kpi.noCycle",
    defaultMessage: "Контуров без агроцикла",
    description: "Подпись KPI: сколько контуров с посевом, где агроцикл ещё не начат",
  },
  kpiNoCycleNote: {
    id: "canonAgrocycle.candidates.kpi.noCycleNote",
    defaultMessage: "в базе у предварительного агроцикла строки нет: он собирается из размещения культуры на контуре и действующего шаблона",
    description: "Мелкая подпись под KPI «Контуров без агроцикла»",
  },
  kpiReady: {
    id: "canonAgrocycle.candidates.kpi.ready",
    defaultMessage: "Можно начать сегодня",
    description: "Подпись KPI: у скольких контуров агроцикл можно начать сегодня",
  },
  kpiReadyNote: {
    id: "canonAgrocycle.candidates.kpi.readyNote",
    defaultMessage: "остальные — вне защитного окна сезона или без действующего шаблона",
    description: "Мелкая подпись под KPI «Можно начать сегодня»",
  },
  kpiCatalog: {
    id: "canonAgrocycle.candidates.kpi.catalog",
    defaultMessage: "Видов работ в каталоге",
    description: "Подпись KPI: сколько видов работ в каталоге",
  },
  kpiCatalogNote: {
    id: "canonAgrocycle.candidates.kpi.catalogNote",
    defaultMessage: "вид работы не знает культуры: полив — это полив и для хлопка, и для пшеницы",
    description: "Мелкая подпись под KPI «Видов работ в каталоге»",
  },
  startAlert: {
    id: "canonAgrocycle.candidates.startAlert",
    defaultMessage: "Агроцикл не начат",
    description: "Заголовок ошибки: агроцикл начать не удалось; ниже — текст ошибки",
  },
  candEmpty: {
    id: "canonAgrocycle.candidates.empty",
    defaultMessage: "Контуров без агроцикла нет",
    description: "Заголовок пустого списка вкладки «К началу»",
  },
  candCaption: {
    id: "canonAgrocycle.candidates.caption",
    defaultMessage: "Окно начала показано ДО клика: защитное окно сезона не даст начать агроцикл пшеницы летом, и лучше прочитать об этом заранее, чем получить отказ после попытки.",
    description: "Подпись (для чтения с экрана) таблицы вкладки «К началу»",
  },
  colWindowStart: {
    id: "canonAgrocycle.candidates.colWindow",
    defaultMessage: "Окно начала",
    description: "Заголовок колонки: между какими датами агроцикл можно начать",
  },
  colReady: {
    id: "canonAgrocycle.candidates.colReady",
    defaultMessage: "Готовность",
    description: "Заголовок колонки: можно ли сейчас начать агроцикл",
  },
  startButton: {
    id: "canonAgrocycle.candidates.start",
    defaultMessage: "Начать агроцикл",
    description: "Кнопка в строке контура: начать агроцикл (менеджер хокимията); коротко",
  },
  unknownCropHint: {
    id: "canonAgrocycle.cropUnknownHint",
    defaultMessage: "код культуры не из словаря приложения",
    description: "Подсказка у бейджа с кодом культуры, которой нет в словаре",
  },
  windowRange: {
    id: "canonAgrocycle.candidates.windowRange",
    defaultMessage: "{from} — {to}",
    description: "Окно начала агроцикла; {from}, {to} — даты",
  },
});

const TAB_PARAM = "tab";
const TEMPLATE_PARAM = "template_id";
const CYCLE_PARAM = "cycle_id";

type CycleTab = "templates" | "cycles" | "candidates";

const TABS: { key: CycleTab; label: PlainMessage }[] = [
  { key: "templates", label: m.tabTemplates },
  { key: "cycles", label: m.tabCycles },
  { key: "candidates", label: m.tabCandidates },
];

export function AgrocycleScreen() {
  const t = useT();
  const context = useAnalysisContext();
  const [params, setParams] = useSearchParams();
  const raw = params.get(TAB_PARAM);
  const tab: CycleTab =
    raw === "cycles" || raw === "candidates" || raw === "templates"
      ? raw
      : "templates";

  function select(next: CycleTab) {
    const copy = new URLSearchParams(params);
    copy.set(TAB_PARAM, next);
    copy.delete(TEMPLATE_PARAM);
    copy.delete(CYCLE_PARAM);
    setParams(copy, { replace: true });
  }

  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={t(m.purpose)}
      />

      {/* Вкладки — общий примитив, а не своя разметка. Самодельный список
          выше давал `role="tab"` без стрелок, без Home/End и без связки с
          панелью: клавиатурой до вкладки доходили, а перейти между ними не
          могли. Вид при этом остаётся в URL — им делятся ссылкой. */}
      <Tabs
        selectedKey={tab}
        onSelectionChange={(key) => select(key as CycleTab)}
      >
        <TabList label={t(m.tabsLabel)}>
          {TABS.map((item) => (
            <Tab key={item.key} id={item.key}>
              {t(item.label)}
            </Tab>
          ))}
        </TabList>
        <TabPanel id="templates">
          <TemplatesTab />
        </TabPanel>
        <TabPanel id="cycles">
          <CyclesTab />
        </TabPanel>
        <TabPanel id="candidates">
          <CandidatesTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Шаблоны: версии и доступность их работ                              */
/* ------------------------------------------------------------------ */

function TemplatesTab() {
  const t = useT();
  const context = useAnalysisContext();
  const [params, setParams] = useSearchParams();
  const chosen = params.get(TEMPLATE_PARAM);
  const list = useRequest<AgrocycleTemplates>(
    () => api.agrocycleTemplates(context.params),
    [context.key],
  );

  function select(id: number | null) {
    const copy = new URLSearchParams(params);
    if (id === null) copy.delete(TEMPLATE_PARAM);
    else copy.set(TEMPLATE_PARAM, String(id));
    setParams(copy, { replace: true });
  }

  if (list.error && !list.data) {
    return (
      <RequestErrorPanel
        error={list.error}
        title={t(m.tplFailed)}
        action={<Button onClick={list.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!list.data) {
    return <DataStatePanel state="loading" title={t(m.tplLoading)} />;
  }
  if (list.data.items.length === 0) {
    return (
      <DataStatePanel state="empty" title={t(m.tplEmpty)}>
        {t(m.tplEmptyText)}
      </DataStatePanel>
    );
  }

  return (
    <div className={features.stack}>
      <div className={features.scrollX}>
        <table className={features.plainTable}>
          <caption>{t(m.tplCaption)}</caption>
          <thead>
            <tr>
              <th>{t(m.colCrop)}</th>
              <th>{t(m.colSeason)}</th>
              <th>{t(m.colTemplate)}</th>
              <th>{t(m.colStatus)}</th>
              <th>{t(m.colActiveFrom)}</th>
              <th>{t(m.colWorks)}</th>
              <th>{t(m.colOpen)}</th>
              <th>{t(m.colCycles)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((row) => (
              <TemplateListRow
                key={row.id}
                row={row}
                active={chosen === String(row.id)}
                onSelect={() =>
                  select(chosen === String(row.id) ? null : row.id)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
      {chosen ? (
        <TemplateDetail id={Number(chosen)} onClose={() => select(null)} />
      ) : null}
    </div>
  );
}

function TemplateListRow({
  row,
  active,
  onSelect,
}: {
  row: TemplateRow;
  active: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  const life = lifecycleView(row.lifecycle);
  return (
    <tr>
      <td>
        <CropName code={row.crop_code} />
      </td>
      <td>{row.season_label}</td>
      <td>{row.version}</td>
      <td>
        <Badge tone={life.editable ? "neutral" : "source"} title={life.hint}>
          {life.label}
        </Badge>{" "}
        <EvidenceBadge status={row.epistemic_status} />
      </td>
      <td>
        <Text value={row.published_on ? formatDate(row.published_on) : null} />
      </td>
      <td className={features.num}>
        <Count value={row.slots} />
      </td>
      <td className={features.num}>
        <Count value={row.open_slots} />
      </td>
      <td className={features.num}>
        <Count value={row.instances} />
      </td>
      <td>
        <Button variant={active ? "primary" : "secondary"} onClick={onSelect}>
          {active ? t(m.collapse) : t(m.showWorks)}
        </Button>
      </td>
    </tr>
  );
}

function TemplateDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const canManage = auth.principal?.can_manage_campaigns ?? false;
  const [override, setOverride] = useState<AgrocycleTemplateDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const loaded = useRequest<AgrocycleTemplateDetail>(
    () => api.agrocycleTemplate(id, context.params),
    [context.key, id],
  );
  const data = override ?? loaded.data;

  async function toggle(slot: SlotOut, action: string, reason: string) {
    if (slot.work_def_id === null) return;
    setBusy(true);
    setFailure(null);
    try {
      const next = await api.setSlotAvailability(
        slot.work_def_id,
        {
          action,
          effective_on: (context.params.as_of as string) ?? today(),
          reason,
        },
        context.params,
      );
      setOverride(next);
    } catch (error) {
      setFailure(
        error instanceof Error ? error.message : t(m.decisionNotSaved),
      );
    } finally {
      setBusy(false);
    }
  }

  if (loaded.error && !data) {
    return (
      <RequestErrorPanel
        error={loaded.error}
        title={t(m.tplDetailFailed)}
        action={<Button onClick={loaded.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!data) {
    return <DataStatePanel state="loading" title={t(m.tplDetailLoading)} />;
  }

  const life = lifecycleView(data.template.lifecycle);
  const frozen = frozenNote(data.template);

  return (
    <Card
      large
      title={
        <>
          {data.template.crop_code
            ? t(m.tplTitleCrop, { crop: cropLabel(data.template.crop_code).label })
            : t(m.tplTitleNoCrop)}{" "}
          <EvidenceBadge status={data.template.epistemic_status} />
        </>
      }
    >
      <div className={features.rowWrap}>
        <Badge tone={life.editable ? "neutral" : "source"} title={life.hint}>
          {life.label}
        </Badge>
        {frozen ? <Badge tone="attention">{frozen}</Badge> : null}
        <Button onClick={onClose}>{t(m.collapse)}</Button>
      </div>

      {!life.editable ? (
        <p className={styles.slotHint}>{t(m.frozenNote, { hint: life.hint })}</p>
      ) : null}

      {!canManage ? (
        <InlineAlert title={t(m.manageTitle)}>
          {t(m.manageText)}
        </InlineAlert>
      ) : null}
      {failure ? (
        <InlineAlert title={t(m.decisionFailed)}>{failure}</InlineAlert>
      ) : null}

      <div>
        {data.slots.map((slot) => (
          <SlotRow
            key={`${slot.work_def_id ?? "added"}-${slot.order_no ?? 0}`}
            slot={slot}
            canManage={canManage}
            busy={busy}
            onToggle={toggle}
          />
        ))}
      </div>
    </Card>
  );
}

function SlotRow({
  slot,
  canManage,
  busy,
  onToggle,
}: {
  slot: SlotOut;
  canManage: boolean;
  busy: boolean;
  onToggle: (slot: SlotOut, action: string, reason: string) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  const state = slotStateView(slot.state);
  const deadline = windowView(slot);
  const action = availabilityAction(slot);
  const norms = normsView(slot.resource_norms);

  return (
    <div className={styles.slot}>
    <div className={`${styles.slotRow} ${slot.added ? styles.slotAdded : ""}`}>
      <span className={styles.slotOrder}>
        {slot.order_no === null ? "" : slot.order_no}
      </span>
      <span className={styles.slotName}>
        <span>{slot.name}</span>
        <span className={styles.slotHint}>
          {t(m.codeLine, {
            code: slot.catalog_code,
            required: slot.is_required ? t(m.required) : t(m.optional),
          })}
          {slot.cancelled_reason ? ` · ${slot.cancelled_reason}` : ""}
        </span>
      </span>
      <span
        className={deadline.known ? undefined : styles.slotHint}
        title={deadline.hint ?? undefined}
      >
        {deadline.label}
      </span>
      <Badge
        tone={
          state.attention ? "attention" : state.known ? "neutral" : "unknown"
        }
        title={state.hint ?? undefined}
      >
        {state.label}
      </Badge>
      <span>
        {canManage && action ? (
          action.needsReason && !asking ? (
            <Button onClick={() => setAsking(true)}>{action.label}</Button>
          ) : action.needsReason ? (
            <span className={styles.reasonRow}>
              <TextField
                aria-label={t(m.closeReasonLabel)}
                size="sm"
                value={reason}
                placeholder={t(m.closeReasonPlaceholder)}
                onChange={setReason}
                isRequired
              />
              <Button
                variant="primary"
                disabled={busy || reason.trim() === ""}
                onClick={() => {
                  onToggle(slot, action.action, reason);
                  setAsking(false);
                  setReason("");
                }}
              >
                {t(m.confirm)}
              </Button>
              <Button onClick={() => setAsking(false)}>{t(COMMON.cancel)}</Button>
            </span>
          ) : (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => onToggle(slot, action.action, "")}
            >
              {action.label}
            </Button>
          )
        ) : null}
      </span>
    </div>

    {/* Нормы и происхождение — в раскрытии, а не в строке. В строке они
        превращали план из последовательности работ в таблицу цифр, а
        читают её ради другого вопроса: что открыто и что просрочено.
        Свёрнутый вид при этом уже отвечает: сколько норм задано и откуда
        взялась сама работа. */}
    <Disclosure
      tone="inline"
      title={t(m.normsTitle)}
      summary={
        <>
          {norms.empty
            ? t(m.normsNone)
            : t(m.normsCount, { count: norms.entries.length })}{" "}
          <EvidenceBadge status={slot.epistemic_status} />
        </>
      }
    >
      {norms.empty ? (
        <p className={styles.slotHint}>{t(m.normsEmptyText)}</p>
      ) : (
        <dl className={styles.norms}>
          {norms.entries.map((entry) => (
            <div key={entry.key} className={styles.normRow}>
              <dt className={styles.slotHint}>{entry.key}</dt>
              <dd>{entry.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className={styles.slotHint}>
        {slot.evidence_source
          ? t(m.originSource, {
              where: slot.evidence ?? t(m.notSpecified),
              source: slot.evidence_source,
            })
          : t(m.origin, { where: slot.evidence ?? t(m.notSpecified) })}
      </p>
    </Disclosure>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Циклы: план запущенного экземпляра                                  */
/* ------------------------------------------------------------------ */

/**
 * Селект сезона — ФИЛЬТР ВИДА, а не срез.
 *
 * Разница видна на этом же экране: дата состояния (`as_of`) отвечает на вопрос
 * «что мы знали на такое-то число», а сезон — «о каком сезоне идёт речь». Цикл
 * сезона 2025 остаётся циклом сезона 2025 и в сегодняшнем знании, вместе с
 * отчётами, внесёнными после его окна; выбрать его срезом прошлого года значило
 * бы объявить эти отчёты несуществующими.
 *
 * Список меток берётся из САМИХ ЦИКЛОВ среза (`available_seasons` в ответе), а
 * не из версий шаблонов: предлагать сезон, которого в корпусе нет, значит
 * обещать пустой экран, а опубликованный шаблон без единого запущенного цикла —
 * ровно такой сезон. Считает его сервер, потому что список обязан быть посчитан
 * ДО фильтра вида: сузь его выбранным сезоном — и в селекте останется один
 * вариант, тот же, что уже выбран.
 *
 * Выбранный сезон стоит в списке ВСЕГДА, даже когда циклов по нему нет: иначе
 * поле показывало бы «все сезоны» рядом с пустым экраном, объяснённым этим
 * сезоном. Отсутствие данных названо подсказкой варианта, а не его исчезновением
 * — тот же приём, что в районном обзоре.
 */
function SeasonFilter({
  seasons,
  note,
}: {
  seasons: ReadonlyArray<string>;
  note?: string | null;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const chosen = context.view.season;
  const options = [...new Set([...seasons, ...(chosen ? [chosen] : [])])].sort();

  return (
    <div className={layout.filterBar}>
      <Select
        label={t(m.seasonLabel)}
        size="sm"
        explain={t(m.seasonExplain)}
        placeholder={t(m.seasonAll)}
        options={[
          { value: "", label: t(m.seasonAll), hint: t(m.seasonAllHint) },
          ...options.map((value) => ({
            value,
            label: value,
            hint: seasons.includes(value) ? undefined : t(m.seasonNoCycles),
          })),
        ]}
        value={chosen ?? ""}
        onChange={(next) => context.setViewFilter("season", next === "" ? null : next)}
        description={note ?? t(m.seasonDescription)}
      />
    </div>
  );
}

function CyclesTab() {
  const t = useT();
  const context = useAnalysisContext();
  const [params, setParams] = useSearchParams();
  const chosen = params.get(CYCLE_PARAM);
  const list = useRequest<AgrocycleCycles>(
    () => api.agrocycleCycles({ ...context.viewParams, limit: 50 }),
    [context.viewKey],
  );

  function select(id: number | null) {
    const copy = new URLSearchParams(params);
    if (id === null) copy.delete(CYCLE_PARAM);
    else copy.set(CYCLE_PARAM, String(id));
    setParams(copy, { replace: true });
  }

  if (list.error && !list.data) {
    return (
      <RequestErrorPanel
        error={list.error}
        title={t(m.cyclesFailed)}
        action={<Button onClick={list.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!list.data) {
    return <DataStatePanel state="loading" title={t(m.cyclesLoading)} />;
  }
  if (list.data.items.length === 0) {
    return (
      <div className={features.stack}>
        {/* Плашка области стоит ПЕРЕД пустотой намеренно: «циклов нет» и
            «циклов нет у ваших хозяйств» — разные утверждения, и второе без
            неё читается как первое. */}
        <ScopeNotice scope={list.data.context.scope} />
        <SeasonFilter seasons={list.data.available_seasons} />
        <DataStatePanel state="empty" title={t(m.cyclesEmpty)}>
          {/* Причина пустоты приходит от сервера, когда выборка сужена: «по
              сезону 2027 циклов нет» и «циклов не запускали» — разные
              утверждения, и подменять первое вторым нельзя. */}
          {list.data.context.filters?.empty_reason ?? t(m.cyclesEmptyText)}
        </DataStatePanel>
      </div>
    );
  }

  return (
    <div className={features.stack}>
      <ScopeNotice scope={list.data.context.scope} />
      <SeasonFilter seasons={list.data.available_seasons} />
      <CyclesHeadline rows={list.data.items} total={list.data.page.total} />

      <div className={features.scrollX}>
        <table className={features.plainTable}>
          <caption>{t(m.cyclesCaption)}</caption>
          <thead>
            <tr>
              <th>{t(m.colContour)}</th>
              <th>{t(m.colCrop)}</th>
              <th>{t(m.colYear)}</th>
              <th>{t(m.colTemplate)}</th>
              <th>{t(m.colStarted)}</th>
              <th>{t(m.colArea)}</th>
              <th>{t(m.colWaiting)}</th>
              <th>{t(m.colDeviations)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((row) => (
              <tr key={row.id}>
                <td>
                  <Link
                    to={{
                      pathname: levelPath("contour", row.contour_id),
                      search: context.linkSearch(),
                    }}
                  >
                    <Text value={row.contour_no} />
                  </Link>
                </td>
                <td>
                  <CropName code={row.crop_code} />
                </td>
                <td className={features.num}>{row.cycle_year}</td>
                <td>{row.template_version}</td>
                <td>{formatDate(row.started_on)}</td>
                <td className={features.num}>
                  <Area microha={row.area_microha} />
                </td>
                <td className={features.num}>
                  <Count value={demanding(row.by_state)} />
                </td>
                <td className={features.num}>
                  <Count value={row.deviations} />
                </td>
                <td>
                  <Button
                    variant={chosen === String(row.id) ? "primary" : "secondary"}
                    onClick={() =>
                      select(chosen === String(row.id) ? null : row.id)
                    }
                  >
                    {chosen === String(row.id) ? t(m.collapse) : t(m.plan)}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {chosen ? (
        <CycleDetail id={Number(chosen)} onClose={() => select(null)} />
      ) : null}
    </div>
  );
}

/**
 * Первый взгляд вкладки «Запущенные циклы»: четыре величины и ни одной больше.
 *
 * До этого вкладка открывалась таблицей: девять колонок и полсотни строк — это
 * данные, а не ответ. Вопрос уровня здесь один — «где работа встала», — и
 * первым на него отвечают числа.
 *
 * Чего в этих числах НЕТ намеренно: сводной оценки цикла. Сложить «ждут» с
 * «отклонениями» значило бы придумать показатель неблагополучия, которого в
 * корпусе не существует.
 */
function CyclesHeadline({
  rows,
  total,
}: {
  rows: ReadonlyArray<{
    by_state: Record<string, number>;
    deviations: number;
    area_microha: number | null;
  }>;
  total: number;
}) {
  const t = useT();
  const sums = useMemo(() => {
    let waiting = 0;
    let overdue = 0;
    let deviations = 0;
    let area = 0;
    // Площадь без строки в реестре не превращается в ноль: такие циклы
    // считаются отдельно, и сумма честно говорит, скольких в ней нет.
    let areaMissing = 0;
    for (const row of rows) {
      waiting += demanding(row.by_state);
      overdue += row.by_state.overdue ?? 0;
      deviations += row.deviations;
      if (row.area_microha === null) areaMissing += 1;
      else area += row.area_microha;
    }
    return { waiting, overdue, deviations, area, areaMissing };
  }, [rows]);

  return (
    <div className={`${layout.grid} ${layout.grid4}`}>
      <MetricCard
        label={t(m.kpiStarted)}
        value={<Count value={total} />}
        bottom={
          rows.length < total
            ? t(m.kpiStartedCut, { shown: rows.length })
            : t(m.kpiStartedAll)
        }
      />
      <MetricCard
        label={t(m.kpiWaiting)}
        value={<Count value={sums.waiting} />}
        bottom={t(m.kpiWaitingOverdue, { count: sums.overdue })}
        explain={t(m.kpiWaitingExplain)}
      />
      <MetricCard
        label={t(m.kpiDeviations)}
        value={<Count value={sums.deviations} />}
        bottom={t(m.kpiDeviationsNote)}
        explain={t(m.kpiDeviationsExplain)}
      />
      <MetricCard
        label={t(m.kpiArea)}
        value={<Area microha={sums.area} rounded />}
        bottom={
          sums.areaMissing > 0
            ? t(m.kpiAreaMissing, { count: sums.areaMissing })
            : t(m.kpiAreaAll)
        }
      />
    </div>
  );
}

function CycleDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const t = useT();
  const context = useAnalysisContext();
  const detail = useRequest<AgrocycleCycleDetail>(
    () => api.agrocycleCycle(id, context.params),
    [context.key, id],
  );
  const cycle = detail.data?.cycle;
  const buckets = useMemo(
    () => (cycle ? stateBuckets(cycle.by_state) : []),
    [cycle],
  );

  if (detail.error && !detail.data) {
    return (
      <RequestErrorPanel
        error={detail.error}
        title={t(m.planFailed)}
        action={<Button onClick={detail.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!detail.data || !cycle) {
    return <DataStatePanel state="loading" title={t(m.planLoading)} />;
  }

  return (
    <Card
      large
      title={
        <>
          {t(m.cycleTitle, {
            no: cycle.contour_no ?? "—",
            crop: cycle.crop_code ? cropLabel(cycle.crop_code).label : t(m.noCrop),
          })}{" "}
          <EvidenceBadge status={cycle.epistemic_status} />
        </>
      }
    >
      <div className={features.rowWrap}>
        <Badge tone="source" title={t(m.frozenTemplateHint)}>
          {t(m.templateBadge, { name: cycle.template_version })}
        </Badge>
        <Badge tone="neutral">{t(m.startedOn, { date: formatDate(cycle.started_on) })}</Badge>
        {cycle.finished_on ? (
          <Badge tone="neutral">{t(m.finishedOn, { date: formatDate(cycle.finished_on) })}</Badge>
        ) : null}
        <Button onClick={onClose}>{t(m.collapse)}</Button>
      </div>

      <div className={styles.states}>
        {buckets.map((bucket) => (
          <span key={bucket.code} className={styles.stateCell}>
            <span
              className={`${styles.stateCount} ${
                bucket.count === 0 ? styles.stateZero : ""
              }`}
            >
              {bucket.count}
            </span>
            <span className={styles.stateLabel}>{bucket.label}</span>
          </span>
        ))}
      </div>

      {/* Окна работ цикла на одной оси. Из полосы слотов это не читается:
          два срока в ячейке не показывают ни того, что окна перекрываются, ни
          того, что одно уже закрылось раньше линии среза. */}
      <CycleWindows
        slots={detail.data.slots}
        asOf={detail.data.context.as_of ?? detail.data.context.snapshot_state_date}
        epistemic={cycle.epistemic_status}
      />

      <div>
        {detail.data.slots.map((slot) => (
          <SlotRow
            key={`${slot.work_def_id ?? "added"}-${slot.order_no ?? 0}-${slot.catalog_code}`}
            slot={slot}
            canManage={false}
            busy={false}
            onToggle={() => undefined}
          />
        ))}
      </div>

      {detail.data.deviations.length > 0 ? (
        <Card title={t(m.deviationsTitle)}>
          <div className={styles.ledger}>
            {detail.data.deviations.map((row) => (
              <div key={row.id} className={styles.ledgerRow}>
                <span className={styles.ledgerWhen}>
                  {formatDate(row.decided_on)}
                </span>
                <Badge tone="neutral">{row.kind}</Badge>
                <span>{row.work_name}</span>
                <span className={styles.ledgerWhy}>{row.reason}</span>
                <span className={styles.ledgerWhy}>
                  <Text value={row.decided_by} fallback={t(m.authorUnknown)} />
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {detail.data.availability_log.length > 0 ? (
        <Card title={t(m.logTitle)}>
          <div className={styles.ledger}>
            {detail.data.availability_log.map((row) => (
              <div key={row.id} className={styles.ledgerRow}>
                <span className={styles.ledgerWhen}>
                  {formatDate(row.effective_on)}
                </span>
                <Badge tone={row.action === "open" ? "source" : "attention"}>
                  {row.action === "open" ? t(m.logOpened) : t(m.logBlocked)}
                </Badge>
                <span>{row.work_name}</span>
                <span className={styles.ledgerWhy}>
                  {row.scope === "cycle" ? t(m.logCycle) : t(m.logDistrict)}
                </span>
                <span className={styles.ledgerWhy}>
                  <Text value={row.decided_by} fallback={t(m.authorUnknown)} />
                </span>
                {row.reason ? (
                  <span className={styles.ledgerWhy}>{row.reason}</span>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </Card>
  );
}

/**
 * Окна работ цикла на оси времени.
 *
 * Что лента НЕ рисует и почему:
 *
 * * **доступность полосой не откладывается.** Работа открывается решением
 *   хокимията, а не наступлением даты (`AgrocycleScreen`, шапка экрана): у
 *   доступности нет отрезка времени, и нарисованный отрезок соврал бы. Поэтому
 *   доступность стоит отметкой у имени работы — рядом с ней, но не на оси;
 * * **неполный и невычислимый срок остаются словами.** «Без срока» и «срок не
 *   посчитан» — разные утверждения (`windowView`), и оба важнее пустой полосы;
 * * **правее последнего окна ничего нет.** Продолжение вправо читалось бы как
 *   план на остаток сезона, которого в корпусе нет.
 */
function CycleWindows({
  slots,
  asOf,
  epistemic,
}: {
  slots: ReadonlyArray<SlotOut>;
  asOf: string | null;
  epistemic: string | null;
}) {
  const t = useT();
  const rows: TimelineRow[] = slots.map((slot) => {
    const key = `${slot.work_def_id ?? "added"}-${slot.order_no ?? 0}-${slot.catalog_code}`;
    const window = windowView(slot);
    const state = slotStateView(slot.state);
    const dated = slot.window_from !== null && slot.window_to !== null;

    return {
      key,
      label: slot.name,
      labelText: slot.name,
      marks: (
        <>
          <Badge
            tone={slot.availability === "open" ? "source" : "unknown"}
            title={t(m.availOpenHint)}
          >
            {slot.availability === "open" ? t(m.availOpen) : t(m.availClosed)}
          </Badge>
          <Badge tone={state.attention ? "attention" : "neutral"} title={state.hint ?? undefined}>
            {state.label}
          </Badge>
        </>
      ),
      bands: dated
        ? [
            {
              key: `window-${key}`,
              from: slot.window_from as string,
              to: slot.window_to as string,
            },
          ]
        : [],
      absent: window.label,
      note: slot.shifted ? t(m.shiftedNote) : undefined,
    };
  });

  const dated = rows.filter((row) => row.bands.length > 0).length;

  return (
    <Timeline
      title={t(m.windowsTitle)}
      denominator={t(m.windowsDenominator, { count: slots.length })}
      rows={rows}
      asOf={asOf}
      asOfAbsent={t(m.windowsNoAsOf)}
      epistemic={epistemic}
      empty={t(m.windowsEmpty)}
      caption={
        <>
          {t(m.windowsCaption)}
          {dated < rows.length
            ? ` ${t(m.windowsNoBand, { count: rows.length - dated })}`
            : ""}
        </>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* К запуску: предварительный цикл, которого нет в базе                */
/* ------------------------------------------------------------------ */

function CandidatesTab() {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const canManage = auth.principal?.can_manage_campaigns ?? false;
  const [busy, setBusy] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const list = useRequest<AgrocycleCandidates>(
    () => api.agrocycleCandidates({ ...context.params, limit: 50 }),
    [context.key],
  );
  const catalog = useRequest<AgrocycleCatalog>(
    () => api.agrocycleCatalog(context.params),
    [context.key],
  );

  async function start(row: CandidateRow) {
    setBusy(row.occupancy_id);
    setFailure(null);
    try {
      await api.startAgrocycle(
        {
          occupancy_id: row.occupancy_id,
          on_date: (context.params.as_of as string) ?? today(),
        },
        context.params,
      );
      list.reload();
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t(m.startFailed));
    } finally {
      setBusy(null);
    }
  }

  if (list.error && !list.data) {
    return (
      <RequestErrorPanel
        error={list.error}
        title={t(m.candFailed)}
        action={<Button onClick={list.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!list.data) {
    return <DataStatePanel state="loading" title={t(m.candLoading)} />;
  }

  const ready = list.data.items.filter((row) => row.may_start).length;

  return (
    <div className={features.stack}>
      <ScopeNotice scope={list.data.context.scope} />
      {/* Сезон стоит в адресе всего приложения, а кандидаты его не сужают:
          у занятости без запущенного цикла метки сезона не существует. Пока
          причину не печатали, человек, выбравший сезон на соседней вкладке,
          видел здесь несуженный список и ни одного слова об этом. */}
      <NotAppliedNotice
        filters={list.data.context.filters}
        chosen={{
          season: context.view.season !== null,
          massif_id: context.view.massifId !== null,
          actor_id: context.view.actorId !== null,
        }}
      />
      <div className={`${layout.grid} ${layout.grid3}`}>
        <MetricCard
          label={t(m.kpiNoCycle)}
          value={<Count value={list.data.page.total} />}
          bottom={t(m.kpiNoCycleNote)}
        />
        <MetricCard
          label={t(m.kpiReady)}
          value={<Count value={ready} />}
          bottom={t(m.kpiReadyNote)}
        />
        <MetricCard
          label={t(m.kpiCatalog)}
          value={<Count value={catalog.data?.items.length ?? null} />}
          bottom={t(m.kpiCatalogNote)}
        />
      </div>

      {failure ? (
        <InlineAlert title={t(m.startAlert)}>{failure}</InlineAlert>
      ) : null}

      {list.data.items.length === 0 ? (
        <DataStatePanel state="empty" title={t(m.candEmpty)} />
      ) : (
        <div className={features.scrollX}>
          <table className={features.plainTable}>
            <caption>{t(m.candCaption)}</caption>
            <thead>
              <tr>
                <th>{t(m.colContour)}</th>
                <th>{t(m.colCrop)}</th>
                <th>{t(m.colYear)}</th>
                <th>{t(m.colArea)}</th>
                <th>{t(m.colTemplate)}</th>
                <th>{t(m.colWindowStart)}</th>
                <th>{t(m.colReady)}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((row) => {
                const hint = startHint(row);
                return (
                  <tr key={row.occupancy_id}>
                    <td>
                      <Link
                        to={{
                          pathname: levelPath("contour", row.contour_id),
                          search: context.linkSearch(),
                        }}
                      >
                        <Text value={row.contour_no} />
                      </Link>
                    </td>
                    <td>
                      <CropName code={row.crop_code} />
                    </td>
                    <td className={features.num}>{row.cycle_year}</td>
                    <td className={features.num}>
                      <Area microha={row.area_microha} />
                    </td>
                    <td>
                      <Text value={row.template_version} />
                    </td>
                    <td>
                      {row.guard ? (
                        t(m.windowRange, {
                          from: formatDate(row.guard.start_from),
                          to: formatDate(row.guard.start_to),
                        })
                      ) : (
                        <Text value={null} />
                      )}
                    </td>
                    <td>
                      <Badge
                        tone={hint.ready ? "source" : "attention"}
                        title={hint.hint}
                      >
                        {hint.label}
                      </Badge>
                    </td>
                    <td>
                      {canManage ? (
                        <Button
                          variant="primary"
                          disabled={!row.may_start || busy === row.occupancy_id}
                          onClick={() => start(row)}
                        >
                          {t(m.startButton)}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Имя культуры по коду.
 *
 * Словарь культур один на всё приложение и незнакомый код НЕ подменяет
 * похожим знакомым: он показывается как неизвестный, и подпись здесь обязана
 * совпадать с подписью на карте и в карточке контура.
 */
function CropName({ code }: { code: string | null }) {
  const t = useT();
  if (!code) return <Text value={null} />;
  const crop = cropLabel(code);
  return crop.known ? (
    <>{crop.label}</>
  ) : (
    <Badge tone="unknown" title={t(m.unknownCropHint)}>
      {crop.label}
    </Badge>
  );
}

/** Сегодня в ISO — когда контекст анализа не назвал дату явно. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}
